// Shared dashboard sheet: KPI card, data-table, filters, chart header, legend.
// Tokens live on :root in theme.css and inherit into each shadow root.
import { css } from 'lit';

/** Green = retrievable, red = blocked, grey = unknown. */
export const flightMarkStyles = css`
  .flight-mark {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    color: var(--dash-muted, #8fa3b8);
  }
  .flight-dot {
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 50%;
    flex: none;
    background: #6d8299;
    box-shadow: 0 0 0 3px rgba(109, 130, 153, 0.22);
  }
  .flight-mark.good { color: #8fd0a8; }
  .flight-mark.good .flight-dot {
    background: #3dbe74;
    box-shadow: 0 0 0 3px rgba(61, 190, 116, 0.28);
  }
  .flight-mark.bad { color: #f0a0a0; }
  .flight-mark.bad .flight-dot {
    background: #e15b5b;
    box-shadow: 0 0 0 3px rgba(225, 91, 91, 0.28);
  }
  .flight-mark.unknown { color: #8fa3b8; }
  .contact-form {
    margin-top: 0.85rem;
    padding: 12px;
    border: 1px solid var(--dash-border, #314255);
    border-radius: 10px;
    background: var(--dash-elevated, #182430);
    display: grid;
    gap: 10px;
  }
  .contact-form .name-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .contact-form label {
    display: grid;
    gap: 4px;
    margin: 0;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--dash-muted, #8fa3b8);
  }
  .contact-form input {
    width: 100%;
    box-sizing: border-box;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    font-size: 0.875rem;
  }
  .contact-form .save-contact { justify-self: start; }
  .contact-list { display: grid; gap: 6px; margin-top: 4px; }
  .contact-person {
    padding: 8px 10px;
    border-radius: 8px;
    background: #0f1419;
  }
  @media (max-width: 800px) {
    .contact-form .name-row { grid-template-columns: 1fr; }
  }
`;

/** Tail detail dialog. Desktop is a centered card; narrow screens use nearly the full viewport. */
export const tailDialogStyles = css`
  dialog.tail-detail {
    width: min(720px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
    max-height: min(84vh, 760px);
    margin: auto;
    box-sizing: border-box;
    border: 1px solid var(--dash-border);
    border-radius: 10px;
    background: var(--dash-surface);
    color: var(--dash-text);
    font-family: var(--dash-font, inherit);
    font-size: 0.875rem;
    line-height: 1.45;
    padding: 16px 18px 14px;
  }
  dialog.tail-detail::backdrop {
    background: rgba(0, 0, 0, 0.45);
  }
  dialog.tail-detail h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.25;
    color: var(--dash-accent);
  }
  .detail-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }
  .detail-head > div {
    flex: 1;
    min-width: 0;
  }
  .detail-tools {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
  }
  .detail-kicker,
  dialog.tail-detail dt {
    margin: 0;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--dash-muted);
  }
  .detail-kicker { margin-bottom: 4px; }
  .detail-sub,
  dialog.tail-detail .muted,
  .detail-note {
    color: var(--dash-muted);
    font-size: 0.875rem;
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
    line-height: 1.45;
  }
  .detail-note { margin: 0 0 12px; font-size: 0.8rem; }
  dialog.tail-detail dd {
    margin: 0.2rem 0 0;
    font-size: 0.875rem;
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
    line-height: 1.45;
    color: var(--dash-text);
  }
  dialog.tail-detail dt { margin-top: 0.85rem; }
  .detail-scroll {
    max-height: min(64vh, 620px);
    overflow: auto;
    padding-right: 4px;
  }
  dialog.confirm {
    width: min(440px, calc(100vw - 32px));
    margin: auto;
    box-sizing: border-box;
    border: 1px solid var(--dash-border);
    border-radius: 10px;
    background: var(--dash-surface);
    color: var(--dash-text);
    font-family: var(--dash-font, inherit);
    font-size: 0.875rem;
    line-height: 1.45;
    padding: 16px 18px 14px;
  }
  dialog.confirm::backdrop { background: rgba(0, 0, 0, 0.45); }
  dialog.confirm h3 {
    margin: 0 0 8px;
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.3;
    color: var(--dash-text);
  }
  .confirm-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 14px;
  }
  dialog.tail-detail a.external,
  dialog.tail-detail button.external,
  dialog.tail-detail .dialog-close,
  dialog.confirm button {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--dash-border);
    border-radius: 8px;
    background: var(--dash-surface);
    color: var(--dash-text);
    font: 600 0.8125rem/1 var(--dash-font, inherit);
    letter-spacing: normal;
    text-transform: none;
    text-decoration: none;
    cursor: pointer;
  }
  dialog.tail-detail a.external,
  dialog.tail-detail button.external { color: var(--dash-accent); }
  dialog.confirm button.yes {
    background: var(--dash-accent);
    border-color: var(--dash-accent);
    color: #0f1419;
  }
  dialog.tail-detail a.external:hover,
  dialog.tail-detail button.external:hover,
  dialog.tail-detail .dialog-close:hover,
  dialog.confirm button:hover {
    border-color: var(--dash-accent);
    background: var(--dash-accent-soft);
    color: var(--dash-accent);
  }
  dialog.confirm button.yes:hover {
    background: var(--dash-accent);
    color: #0f1419;
  }
  @media (max-width: 800px) {
    dialog.tail-detail {
      width: calc(100vw - 16px);
      max-width: calc(100vw - 16px);
      max-height: calc(100dvh - 16px);
      padding: 14px 12px 12px;
    }
    .detail-head { flex-direction: column; }
    .detail-tools { justify-content: flex-start; }
    .detail-scroll { max-height: calc(100dvh - 148px); }
  }
`;

/** Windows paints the native menu white while dark-mode text stays light. Mac ignores this. */
export const windowsSelectFix = css`
  :host-context(.os-windows) select option {
    color-scheme: light;
    background-color: #ffffff;
    color: #14202b;
  }
`;

export const dashboardStyles = css`
  .warn-chip {
    flex: none;
    padding: 2px 9px;
    border-radius: 999px;
    border: 1px solid color-mix(in srgb, var(--dash-warn) 55%, transparent);
    background: color-mix(in srgb, var(--dash-warn) 8%, var(--dash-surface));
    color: var(--dash-warn);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .finish-tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    padding: 2px 7px;
    border-radius: 5px;
    white-space: nowrap;
  }
  .finish-tag-all {
    background: var(--dash-tag-all-bg);
    color: var(--dash-tag-all-fg);
  }
  .finish-tag-year {
    background: var(--dash-accent-soft);
    color: var(--dash-accent-2);
  }
  td .finish-tag {
    margin-left: 8px;
  }

  .finish-banner {
    margin-bottom: 1rem;
    padding: 0.6rem 0.9rem;
    border: 1px solid color-mix(in srgb, var(--dash-gold) 50%, transparent);
    border-left-width: 4px;
    border-radius: 6px;
    background: color-mix(in srgb, var(--dash-gold) 12%, transparent);
    color: var(--dash-text);
    font-size: 0.85rem;
    font-weight: 500;
  }

  .w-note {
    margin: 0 0 8px;
    color: var(--dash-muted);
    font-size: 12px;
    line-height: 1.45;
  }

  input,
  select,
  textarea,
  button {
    font-family: inherit;
  }

  .filters {
    --filter-control-h: 44px;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 1rem 1.5rem;
    background: var(--dash-surface);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .filter-label {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--dash-muted);
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  .input {
    box-sizing: border-box;
    height: var(--filter-control-h, 44px);
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-elevated-2);
    color: var(--dash-text);
    min-width: 220px;
    transition: all 0.2s ease;
    font-size: 0.9375rem;
  }
  .input:focus {
    outline: none;
    border-color: var(--dash-accent);
    box-shadow: 0 0 0 3px var(--dash-accent-soft);
    background: var(--dash-input-bg-focus);
  }
  .input::placeholder {
    color: var(--dash-faint);
  }

  .date-input {
    box-sizing: border-box;
    height: var(--filter-control-h, 44px);
    min-width: 0;
    padding: 0.4rem 0.6rem;
    font-size: 0.8125rem;
  }

  .date-controls {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .date-presets,
  .date-inputs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }
  .date-presets .filter-btn {
    height: auto;
    padding: 0.5rem 0.55rem;
  }
  .date-inputs {
    align-self: stretch;
    flex-wrap: nowrap;
  }
  .date-inputs .date-input {
    flex: 1;
  }

  .date-separator {
    color: var(--dash-muted);
    font-size: 0.8125rem;
  }

  .filter-btn {
    box-sizing: border-box;
    height: var(--filter-control-h, 44px);
    padding: 0.375rem 0.9rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-elevated-2);
    color: var(--dash-muted);
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    transition: all 0.2s ease;
  }
  .filter-btn:hover {
    border-color: var(--dash-accent);
    background: var(--dash-accent-soft);
    color: var(--dash-accent);
  }
  .filter-btn.active {
    border-color: var(--dash-accent);
    background: var(--dash-accent);
    color: #fff;
  }

  .multi-select-container {
    position: relative;
    min-width: 220px;
    z-index: 10000;
  }

  .multi-select-trigger {
    box-sizing: border-box;
    height: var(--filter-control-h, 44px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-elevated-2);
    color: var(--dash-text);
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.9375rem;
  }
  .multi-select-trigger:hover {
    border-color: var(--dash-accent);
    background: var(--dash-input-bg-focus);
  }

  .multi-select-text {
    flex: 1;
    text-align: left;
  }

  .multi-select-arrow {
    font-size: 10px;
    transition: transform 0.2s ease;
    margin-left: 8px;
  }
  .multi-select-arrow.open {
    transform: rotate(180deg);
  }

  .multi-select-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: var(--dash-elevated);
    border: 1px solid var(--dash-border);
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 10000;
    max-height: 300px;
    display: flex;
    flex-direction: column;
    animation: dropdownFadeIn 0.2s ease;
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .multi-select-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--dash-border);
  }

  .multi-select-actions {
    display: flex;
    gap: 0.25rem;
  }

  .multi-select-clear,
  .multi-select-close {
    background: transparent;
    border: none;
    color: var(--dash-accent);
    cursor: pointer;
    font-size: 0.875rem;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.15s ease;
  }
  .multi-select-clear:hover,
  .multi-select-close:hover {
    background: var(--dash-accent-soft);
  }
  .multi-select-close {
    font-size: 1rem;
    padding: 4px 8px;
  }

  .multi-select-options {
    padding: 8px;
    overflow-y: auto;
    max-height: 240px;
  }

  .multi-select-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    user-select: none;
  }
  .multi-select-option:hover {
    background: var(--dash-accent-soft);
  }
  .multi-select-option input[type='checkbox'] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--dash-accent);
  }
  .multi-select-option span {
    color: var(--dash-text);
    font-size: 0.9375rem;
  }

  select {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-elevated-2);
    color: var(--dash-text);
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem;
  }
  .kpi {
    background: var(--dash-surface);
    background-image: var(--kpi-tint, none);
    padding: 1.5rem;
    border-radius: 10px;
    border: 1px solid var(--dash-border);
    border-top: 3px solid var(--kpi-accent, var(--dash-accent));
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition:
      box-shadow 0.2s ease,
      transform 0.2s ease;
  }
  .kpi:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
  @media (prefers-reduced-motion: reduce) {
    .kpi {
      transition: none;
    }
    .kpi:hover {
      transform: none;
    }
  }
  .kpi-label {
    color: var(--dash-muted);
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.5rem;
  }
  .kpi-label.has-tip {
    cursor: help;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
    text-decoration-color: var(--dash-muted);
  }
  .kpi-value {
    font-size: var(--kpi-value-size, 2rem);
    font-weight: var(--kpi-value-weight, 700);
    color: var(--kpi-value-color, var(--dash-accent));
    line-height: 1.2;
    font-family: var(--dash-font-mono);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    transform-origin: left center;
  }
  .kpi-value-group {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .kpi-value-item {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }
  .kpi-value-label {
    font-size: 1rem;
    color: var(--dash-muted);
    font-weight: 500;
  }
  .kpi-value-number {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--dash-accent);
  }
  .kpi-sub {
    color: var(--dash-muted);
    margin-top: 0.5rem;
    font-size: 0.875rem;
  }
  :host([compact]) .kpi {
    padding: 0.6rem 0.85rem;
    border-radius: 10px;
  }
  :host([compact]) .kpi:hover {
    transform: none;
  }
  :host([compact]) .kpi-label {
    font-size: 0.7rem;
    margin-bottom: 0.2rem;
  }
  :host([compact]) .kpi-value {
    font-size: 1.25rem;
  }
  :host([compact]) .kpi-sub {
    margin-top: 0.2rem;
    font-size: 0.75rem;
  }

  .table {
    background: var(--dash-surface);
    border-radius: 10px;
    border: 1px solid var(--dash-border);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    overflow: auto;
    width: auto;
    max-height: 60vh;
  }
  :host([fill]) {
    display: block;
    height: 100%;
  }
  .dt-wrap.fill {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 20px;
  }
  .table.fill {
    max-height: none;
    flex: 0 1 auto;
    min-height: 0;
  }
  .dt-quick {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 0.75rem;
    padding: 0.6rem 0.9rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-input-bg);
    color: var(--dash-text);
    font-size: 0.9375rem;
  }
  .dt-quick:focus {
    outline: none;
    border-color: var(--dash-accent);
    box-shadow: 0 0 0 3px var(--dash-accent-soft);
  }
  .dt-quick::placeholder {
    color: var(--dash-faint);
  }
  .dt-count {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-height: 26px;
    margin-bottom: 0.5rem;
    margin-left: 0.5rem;
    font-size: 0.8125rem;
    color: var(--dash-muted);
  }
  .dt-clear {
    padding: 3px 9px;
    border-radius: 6px;
    border: 1px solid var(--dash-border);
    background: var(--dash-surface);
    color: var(--dash-text);
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
  }
  .dt-clear:hover {
    border-color: var(--dash-accent);
    color: var(--dash-accent);
  }

  table.data-table {
    border-collapse: separate;
    width: 100%;
    font-size: 0.9375rem;
    color: var(--dash-text);
  }
  table.data-table thead {
    position: sticky;
    top: 0;
    background: var(--dash-elevated);
    z-index: 7;
    backdrop-filter: blur(10px);
  }
  table.data-table thead tr {
    position: sticky;
    top: 0;
    z-index: 6;
    background: var(--dash-elevated);
  }
  table.data-table thead tr.filter-row {
    position: static;
  }
  table.data-table th,
  table.data-table td {
    border-bottom: 1px solid color-mix(in srgb, var(--dash-muted) 30%, transparent);
    padding: 0.5rem 1.25rem;
    text-align: left;
    max-width: 300px;
    vertical-align: top;
    overflow-wrap: break-word;
  }
  table.data-table tr:hover {
    background: var(--dash-accent-soft);
  }
  table.data-table tbody tr {
    transition: background-color 0.15s ease;
  }
  table.data-table tbody tr:nth-child(-n + 30) {
    animation: rowIn 0.3s ease backwards;
  }
  @keyframes rowIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    table.data-table tbody tr:nth-child(-n + 30) {
      animation: none;
    }
  }
  table.data-table th {
    color: var(--dash-muted);
    font-weight: 600;
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background-color: var(--dash-elevated);
  }
  table.data-table td {
    color: var(--dash-text);
    font-family: var(--dash-font);
    font-variant-numeric: tabular-nums;
    transition: background-color 0.3s ease;
  }
  table.data-table td.num,
  table.data-table th.num {
    text-align: right;
  }
  table.data-table td.num {
    font-family: var(--dash-font-mono);
  }
  th.num .th-top {
    justify-content: flex-end;
  }
  table.data-table .rownum {
    width: 1%;
    white-space: nowrap;
    text-align: right;
    color: var(--dash-muted);
    cursor: default;
  }
  table.data-table td.rank-0 {
    background: rgba(63, 185, 80, 0.38);
    font-weight: 600;
  }
  table.data-table td.rank-1 {
    background: rgba(63, 185, 80, 0.24);
  }
  table.data-table td.rank-2 {
    background: rgba(63, 185, 80, 0.12);
  }
  table.data-table .month-row > td {
    background: var(--dash-accent-soft);
    font-weight: 600;
    border-top: 1px solid var(--dash-border);
    border-bottom: 1px solid var(--dash-border);
    position: sticky;
    top: var(--table-header-height, 40px);
    z-index: 5;
  }
  table.data-table tfoot .total-row td {
    position: sticky;
    bottom: 0;
    background: var(--dash-elevated-2);
    color: var(--dash-text);
    font-weight: 700;
    border-top: 2px solid color-mix(in srgb, var(--dash-muted) 60%, transparent);
    z-index: 5;
  }
  table.data-table .sticky-first {
    position: sticky;
    left: 0;
    z-index: 2;
    background: var(--dash-elevated);
  }
  th.th {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s ease;
    position: relative;
  }
  .th-label {
    min-width: 0;
    cursor: pointer;
  }
  .th-label:hover {
    color: var(--dash-accent);
  }
  th.sorted {
    color: var(--dash-accent);
    font-weight: 700;
  }
  table.data-table th.filtered {
    background: var(--dash-accent-soft);
    color: var(--dash-accent);
  }
  .th-top {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .th-sort {
    border: none;
    background: transparent;
    color: var(--dash-faint);
    cursor: pointer;
    padding: 0 2px;
    font-size: 0.85em;
    line-height: 1;
    border-radius: 4px;
    transition: color 0.15s ease;
  }
  .th-sort:hover,
  .th-sort.active {
    color: var(--dash-accent);
  }
  .col-filter-btn {
    border: none;
    background: transparent;
    color: var(--dash-faint);
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    line-height: 0;
    display: inline-flex;
    transition:
      color 0.15s ease,
      background-color 0.15s ease;
  }
  .col-filter-btn:hover {
    color: var(--dash-accent);
    background: var(--dash-accent-soft);
  }
  .col-filter-btn.active {
    color: var(--dash-accent);
  }
  table.data-table tr.filter-row > td {
    background: var(--dash-surface);
    padding: 0.4rem 1.25rem;
    vertical-align: top;
  }
  .col-filter-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    width: 0;
    min-width: 100%;
  }
  .col-filter-in {
    flex: 1 1 60px;
    min-width: 0;
    color-scheme: var(--dash-color-scheme);
    padding: 0.35rem 0.5rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-elevated-2);
    color: var(--dash-text);
    font-size: 0.8125rem;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
  }
  .col-filter-in:focus {
    outline: none;
    border-color: var(--dash-accent);
  }
  .col-filter-in::placeholder {
    color: var(--dash-faint);
  }
  .col-filter-in::-webkit-datetime-edit {
    color: var(--dash-faint);
  }
  .col-select {
    flex: 1 1 100%;
    min-width: 0;
  }
  .col-select-trigger {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.35rem;
    padding: 0.35rem 0.5rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    background: var(--dash-elevated-2);
    color: var(--dash-text);
    font-size: 0.8125rem;
    font-weight: 400;
    text-transform: none;
    letter-spacing: normal;
    cursor: pointer;
  }
  .col-select-trigger:hover {
    border-color: var(--dash-accent);
  }
  .col-select-arrow {
    font-size: 9px;
    color: var(--dash-faint);
    transition: transform 0.2s ease;
  }
  .col-select-arrow.open {
    transform: rotate(180deg);
  }
  .col-select-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9998;
  }
  .col-select-menu {
    position: fixed;
    z-index: 9999;
    max-width: 320px;
    border: 1px solid var(--dash-border);
    border-radius: 10px;
    background: var(--dash-surface);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    animation: dropdownFadeIn 0.15s ease;
  }
  .col-select-actions {
    display: flex;
    gap: 0.25rem;
    padding: 0.25rem 0.35rem;
    border-bottom: 1px solid var(--dash-border);
  }
  .col-select-options {
    max-height: 180px;
    overflow-y: auto;
    padding: 4px;
  }
  .col-select-options .multi-select-option {
    padding: 5px 8px;
    gap: 8px;
    font-size: 0.8125rem;
  }
  .col-select-options .multi-select-option input[type='checkbox'] {
    width: 15px;
    height: 15px;
  }

  .d3-tooltip {
    position: fixed;
    pointer-events: none;
    z-index: 10000;
    opacity: 0;
    background: var(--dash-elevated);
    color: var(--dash-text);
    font: 12px/1.4 var(--dash-font, system-ui), sans-serif;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--dash-border);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    white-space: nowrap;
  }
  .d3-tooltip .tip-title {
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: var(--dash-accent);
  }
  .d3-tooltip .tip-row {
    display: grid;
    grid-template-columns: auto auto;
    gap: 1rem;
  }
  .d3-tooltip .tip-row span {
    color: var(--dash-muted);
  }

  svg text {
    font-family: var(--dash-font);
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  .chart-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--dash-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 0.75rem;
  }
  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    color: var(--dash-text);
    cursor: pointer;
    padding: 4px 0;
    border-radius: 6px;
  }
  .legend-item.active,
  .legend-item:hover {
    color: var(--dash-accent-2);
  }
  .legend-color {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }

  @media (max-width: 960px) {
    .kpis {
      grid-template-columns: 1fr;
    }
    .filters {
      grid-template-columns: 1fr;
    }
    table.data-table th,
    table.data-table td {
      padding: 0.5rem 0.625rem;
      max-width: 180px;
    }
  }
`;
